// Global Types
import type {
  $Component,
} from 'common.types';
import type {
  $FullAdminRegatta,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import {
  Loader,
} from '@vroskus/library-components';

// Helpers
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultGetData,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import RegattaResultTilesSection from '../../sections/RegattaResultsPageSections/RegattaResultTilesSection';
import RegattaResultTablesSection from '../../sections/RegattaResultsPageSections/RegattaResultTablesSection';
import RegattaResultSequenceSection from '../../sections/RegattaResultsPageSections/RegattaResultSequenceSection';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $View = 'sequence' | 'tables' | 'tiles';

type $State = {
  item: $FullAdminRegatta | null;
  loading: boolean;
  view: $View;
};

const pageId = adminPages.regattaResultsPage;

class RegattaResultsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      item: null,
      loading: false,
      view: 'tables',
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getDataWithLoading();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getDataWithLoading();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId && data.modelName === 'RegattaResultTable') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getDataWithLoading(): Promise<void> {
    await this.setStateAsync({
      loading: true,
    });

    await this.getData();
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          const item = await Services.Api.getOneRegatta({
            id: RegattaId,
          });

          await this.setStateAsync({
            item,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;
    const {
      view,
    } = this.state;

    return (
      <div
        className={'mb-3'}
        role={'tabpanel'}
      >
        <Nav
          justified
          tabs
        >
          <NavItem id={'regatta-result-tables'}>
            <NavLink
              className={view === 'tables' ? 'active' : ''}
              onClick={() => this.setState({
                view: 'tables',
              })}
            >
              <h4 className={'mb-0'}>
                <em className={'fa fa-bars d-none d-sm-inline-block mr-2'} />
                <span>{t('Tables')}</span>
              </h4>
            </NavLink>
          </NavItem>
          <NavItem id={'regatta-result-tiles'}>
            <NavLink
              className={view === 'tiles' ? 'active' : ''}
              onClick={() => this.setState({
                view: 'tiles',
              })}
            >
              <h4 className={'mb-0'}>
                <em className={'fa fa-grip-vertical d-none d-sm-inline-block mr-2'} />
                <span>{t('Tiles')}</span>
              </h4>
            </NavLink>
          </NavItem>
          <NavItem id={'regatta-result-sequence'}>
            <NavLink
              className={view === 'sequence' ? 'active' : ''}
              onClick={() => this.setState({
                view: 'sequence',
              })}
            >
              <h4 className={'mb-0'}>
                <em className={'fa fa-arrow-down-short-wide d-none d-sm-inline-block mr-2'} />
                <span>{t('Sequence')}</span>
              </h4>
            </NavLink>
          </NavItem>
        </Nav>
      </div>
    );
  }

  renderRegattaResultTablesSection() {
    const {
      item,
    } = this.state;

    return (
      <div id={adminSections[pageId].tables}>
        {item !== null && (
          <RegattaResultTablesSection
            id={adminSections[pageId].tables}
            item={item}
          />
        )}
      </div>
    );
  }

  renderRegattaResultTilesSection() {
    const {
      item,
    } = this.state;

    return (
      <div id={adminSections[pageId].tiles}>
        {item !== null && (
          <RegattaResultTilesSection
            id={adminSections[pageId].tiles}
            item={item}
          />
        )}
      </div>
    );
  }

  renderRegattaResultSequenceSection() {
    const {
      item,
    } = this.state;

    return (
      <div id={adminSections[pageId].sequence}>
        {item !== null && (
          <RegattaResultSequenceSection
            id={adminSections[pageId].sequence}
            item={item}
          />
        )}
      </div>
    );
  }

  renderContent() {
    const {
      loading,
      view,
    } = this.state;

    const sections: Record<$View, () => $Component<unknown> | null> = {
      sequence: () => this.renderRegattaResultSequenceSection(),
      tables: () => this.renderRegattaResultTablesSection(),
      tiles: () => this.renderRegattaResultTilesSection(),
    };

    return (
      <Loader loading={loading === true}>
        {sections[view]()}
      </Loader>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaResultsPageView;
